import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { adminDashboard } from '@/lib/api'
import { PageMotion } from '@/components/Motion'
import {
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  Panel,
  StatStrip,
} from '@/components/ui'
import { formatNum } from '@/lib/utils'

export default function AdminDashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: adminDashboard,
  })

  if (isLoading) return <LoadingBlock />
  if (error || !data) return <ErrorBlock message="Failed to load admin dashboard" />

  return (
    <PageMotion className="space-y-8">
      <PageHeader
        title="Admin"
        subtitle="Manage missions and imports"
        action={
          <Link to="/admin/upload" className="btn-primary self-start">
            Upload JSON
          </Link>
        }
      />

      <StatStrip
        items={[
          { label: 'Total missions', value: data.totalMissions },
          { label: 'Visible', value: data.visibleMissions },
          { label: 'Hidden', value: data.hiddenMissions },
          { label: 'Kills', value: data.totalKills },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Recent missions">
          <ul className="space-y-0.5">
            {(data.recentMissions ?? []).map(
              (m: { id: number; missionName: string; hidden: boolean; worldname: string }) => (
                <li key={m.id}>
                  <Link
                    to={`/admin/missions/${m.id}`}
                    className="group flex justify-between gap-2 rounded-control px-3 py-2.5 text-sm transition-colors hover:bg-accent-soft"
                  >
                    <span className="truncate text-ink group-hover:text-accent-text transition-colors">
                      {m.missionName || `#${m.id}`}
                      {m.hidden ? (
                        <span className="ml-2 chip text-amber-400 border-amber-500/30 bg-amber-500/10">
                          hidden
                        </span>
                      ) : null}
                    </span>
                    <span className="text-ink-faint shrink-0">{m.worldname}</span>
                  </Link>
                </li>
              ),
            )}
          </ul>
        </Panel>

        <Panel title="Top by kills">
          <ul className="space-y-0.5">
            {(data.topMissionsByKills ?? []).map(
              (m: { missionId: number; missionName: string; killCount: number }) => (
                <li key={m.missionId}>
                  <Link
                    to={`/admin/missions/${m.missionId}`}
                    className="group flex justify-between gap-2 rounded-control px-3 py-2.5 text-sm transition-colors hover:bg-accent-soft"
                  >
                    <span className="truncate text-ink group-hover:text-accent-text transition-colors">
                      {m.missionName || `#${m.missionId}`}
                    </span>
                    <span className="font-mono text-accent-text tabular-nums">
                      {formatNum(m.killCount)}
                    </span>
                  </Link>
                </li>
              ),
            )}
          </ul>
        </Panel>
      </div>
    </PageMotion>
  )
}
